import { extendZodWithOpenApi } from "@asteasolutions/zod-to-openapi/dist/zod-extensions";
import { z } from "zod";

import { assetSchema } from "./asset";

extendZodWithOpenApi(z);

export const testimonialSchema = z.object({
  avatar: z.string().optional(),
  name: z.string(),
  title: z.string(),
  csat: z.number(),
  designerId: z.string().optional(),
  text: z.string().optional(),
});

export type Testimonial = z.infer<typeof testimonialSchema>;

export const enterpriseUserPartialSchema = z.object({
  _id: z.string().optional(),
  name: z.string().optional(),
  email: z.string().optional(),
  avatar: z.string().optional(),
  role: z.string().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  profile: z.record(z.unknown()).optional(),
});

export type EnterpriseUserPartial = z.infer<typeof enterpriseUserPartialSchema>;

export const enterpriseSkillRatingSchema = z.object({
  label: z.string(),
  experience: z.number().int().optional(),
  category: z.string().optional(),
});

export type EnterpriseSkillRating = z.infer<typeof enterpriseSkillRatingSchema>;

export enum EnterpriseProfileTagType {
  NoRatedOptions = "no-rated-options",
  RatedOptions = "rated-options",
  TextInput = "text",
}

export const enterpriseRatingMapSchema = z.object({
  label: z.string(),
  value: z.coerce.number().optional(),
});

export type EnterpriseRatingMap = z.infer<typeof enterpriseRatingMapSchema>;

export const aiAssetSchema = assetSchema
  .extend({
    publicUrl: z.string().optional(),
    processedFiles: z.array(z.string()).optional(),
    source: z.string().optional(),
  })
  .refine(
    (data) => {
      return data.publicUrl || data.source;
    },
    {
      message: "publicUrl or source is required",
    },
  );

export const enterpriseCommentsAssistantSchema = z.object({
  assistantId: z.string().optional(), // OpenAI Assistant ID
  summary: z.string().optional(),
  instructions: z.string().optional(),
  files: z.array(aiAssetSchema).optional(),
});

export type EnterpriseCommentsAssistant = z.infer<
  typeof enterpriseCommentsAssistantSchema
>;

export const enterpriseBriefGuidelines = z.object({
  description: z.string().optional(),
  files: z.array(aiAssetSchema).optional(),
  assistantId: z.string().optional(), // OpenAI Assistant ID
  summary: z.string().optional(),
  checklist: z.string().optional(),
  runId: z.string().optional(),
  templates: z.array(aiAssetSchema).optional(),
});

export type EnterpriseBriefGuidelines = z.infer<
  typeof enterpriseBriefGuidelines
>;

export const enterpriseAIPersonas = z.object({
  name: z.string(),
  enabled: z.boolean(),
  default: z.boolean(),
  checklist: z.string().optional(),
});

export type EnterpriseAIPersonas = z.infer<typeof enterpriseAIPersonas>;

export const skillDocumentSchema = z.object({
  documentId: z.string().uuid(),
  s3Key: z.string(),
  name: z.string().max(200),
  mimeType: z.string(),
  sizeBytes: z.number().int().positive().openapi({ exclusiveMinimum: 0 }),
  tokenCount: z.number().int().nonnegative().optional(),
  uploadedAt: z.string(),
  description: z.string().max(500).optional(),
});
export type SkillDocument = z.infer<typeof skillDocumentSchema>;

export const enterpriseAskPunttSkillSchema = z.object({
  skillId: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  customInstructions: z.string().optional(),
  researchUrls: z.string().optional(),
  documents: z.array(skillDocumentSchema).max(10).optional(),
  requiredInputsDescription: z.string().max(2000).optional(),
});

export type EnterpriseAskPunttSkill = z.infer<
  typeof enterpriseAskPunttSkillSchema
>;

export const defaultAskPunttSkills: EnterpriseAskPunttSkill[] = [
  { skillId: "artwork_review", name: "Artwork Review", enabled: true },
  { skillId: "compare", name: "Compare", enabled: true },
  { skillId: "review", name: "Review", enabled: true },
  { skillId: "brief_creation", name: "Brief Writing", enabled: false },
  { skillId: "compliance_review", name: "Compliance Review", enabled: true },
  { skillId: "claim_generation", name: "Claims Verification", enabled: true },
  { skillId: "project_workflow", name: "Projects Workflow", enabled: false },
  { skillId: "meeting_roi", name: "ROI Workshop", enabled: true },
];

export const enterpriseAskPunttConnectorSchema = z.object({
  connectorId: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  connected: z.boolean().optional(),
});

export type EnterpriseAskPunttConnector = z.infer<
  typeof enterpriseAskPunttConnectorSchema
>;

export const enterpriseCustomWorkflowSchema = z.object({
  workflowId: z.string(),
  name: z.string(),
  trigger: z.string(),
  steps: z.string(),
  toolAccess: z.array(z.string()).optional(),
  webSearchEnabled: z.boolean().optional(),
  webSearchDomains: z.array(z.string()).optional(),
  enabled: z.boolean(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  documents: z.array(skillDocumentSchema).max(10).optional(),
  requiredInputsDescription: z.string().max(2000).optional(),
});

export type EnterpriseCustomWorkflow = z.infer<
  typeof enterpriseCustomWorkflowSchema
>;

export const defaultAskPunttConnectors: EnterpriseAskPunttConnector[] = [
  { connectorId: "email", name: "Email", enabled: true, connected: true },
  { connectorId: "wrike", name: "Wrike", enabled: true, connected: false },
  {
    connectorId: "sharepoint",
    name: "SharePoint",
    enabled: true,
    connected: false,
  },
  { connectorId: "asana", name: "Asana", enabled: true, connected: false },
  { connectorId: "figma", name: "Figma", enabled: true, connected: false },
  {
    connectorId: "google_drive",
    name: "Google Drive",
    enabled: true,
    connected: false,
  },
  { connectorId: "canva", name: "Canva", enabled: true, connected: false },
  { connectorId: "jira", name: "Jira", enabled: true, connected: false },
  // connectorId must equal the lowercased Nango provider_config_key so the
  // live connected-apps check (getNangoConnectedApps) lights it up.
  {
    connectorId: "microsoft-teams",
    name: "Microsoft Teams",
    enabled: true,
    connected: false,
  },
];

export const enterpriseAIPersonasUpdate = z.object({
  name: z.string(),
  enabled: z.boolean().optional(),
  default: z.boolean().optional(),
});

export type EnterpriseAIPersonasUpdate = z.infer<
  typeof enterpriseAIPersonasUpdate
>;

export const catalystTeamSettingsSchema = z.object({
  briefGuidelines: enterpriseBriefGuidelines.optional(),
  complianceGuidelines: enterpriseBriefGuidelines.optional(),
  guidelines: enterpriseBriefGuidelines.optional(),
  commentsAssistant: enterpriseCommentsAssistantSchema.optional(),
  objectives: z.string().optional(),
  aiPersonas: z.array(enterpriseAIPersonas).optional(),
  askPunttEnabled: z.boolean().optional(),
  askPunttSkills: z.array(enterpriseAskPunttSkillSchema).optional(),
  askPunttConnectors: z.array(enterpriseAskPunttConnectorSchema).optional(),
  customWorkflows: z.array(enterpriseCustomWorkflowSchema).optional(),
});

export type CatalystTeamSettings = z.infer<typeof catalystTeamSettingsSchema>;

export enum AIProvider {
  OpenAI = "openai",
  OpenAIReasoning = "openai-reasoning",
  Google = "google",
  Anthropic = "anthropic",
}

export const enterpriseProfileSettingsSchema =
  catalystTeamSettingsSchema.extend({
    skills: z.array(enterpriseSkillRatingSchema).optional(),
    industries: z.array(enterpriseSkillRatingSchema).optional(),
    tools: z.array(enterpriseSkillRatingSchema).optional(),
    softSkills: z.array(enterpriseSkillRatingSchema).optional(),
    growthAreas: z.array(z.string()).optional(),
    ratings: z.array(enterpriseRatingMapSchema).optional(),

    // Catalyst settings
    aiProviders: z.nativeEnum(AIProvider).array().optional(),
    delayedMailTime: z.coerce.number().optional(),
    extraFonts: z.array(z.string()).optional(),
  });

export type EnterpriseProfileSettings = z.infer<
  typeof enterpriseProfileSettingsSchema
>;

export const enterpriseTeamSchema = z.object({
  _id: z.string().optional(),
  users: z.array(z.union([z.string(), enterpriseUserPartialSchema])),
  profileSettings: catalystTeamSettingsSchema.optional(),
});

export const wrikeTaskStatusEnum = z.enum([
  "Active",
  "Completed",
  "Deferred",
  "Cancelled",
]);

export const integrationTriggerConfigSchema = z.object({
  eventType: z.string().optional(),
  statusName: z.string().optional(),
  skipPunttUserCheck: z.boolean().optional(),
  assignSelfOnTrigger: z.boolean().optional(),
});

export type IntegrationTriggerConfig = z.infer<
  typeof integrationTriggerConfigSchema
>;

export const integrationCommentBehaviorSchema = z.object({
  postProgressComments: z.boolean().optional(), // When false, skip in-progress/error comments (default: true)
  postCompletionComments: z.boolean().optional(), // When false, skip completion/share-link comments (default: true)
  skipAllComments: z.boolean().optional(), // When true, skip ALL comments (overrides granular controls)
  skipLinkUpdates: z.boolean().optional(), // When true, skip share-link comments only
});

export type IntegrationCommentBehavior = z.infer<
  typeof integrationCommentBehaviorSchema
>;

// NOTE: designReviewRules are partially implemented (christo queue only) — disabled in UI for now
export const designReviewRuleSchema = z.object({
  descriptionContains: z.string(),
  tagUsersOnCompletion: z.boolean().optional(),
  skipStatusUpdate: z.boolean().optional(),
});

export type DesignReviewRule = z.infer<typeof designReviewRuleSchema>;

export const wrikeSchema: z.ZodType = z.object({
  enabled: z.boolean().optional(),
  user: z.string().optional(),
  responseActions: z
    .object({
      updateTaskStatus: z.union([wrikeTaskStatusEnum, z.string()]).optional(),
      inProgressTaskStatus: z.string().optional(),
      tagUsers: z
        .array(
          z.object({
            email: z.string(),
          }),
        )
        .optional(),
    })
    .optional(),
  triggerConfig: integrationTriggerConfigSchema.optional(),
  commentBehavior: integrationCommentBehaviorSchema.optional(),
  designReviewRules: z.array(designReviewRuleSchema).optional(),
});

export type WrikeIntegration = z.infer<typeof wrikeSchema>;

export const asanaWebhookSchema = z.object({
  webhookId: z.string(),
  projectGid: z.string(),
  projectName: z.string(),
  secret: z.string(),
  active: z.boolean(),
  createdAt: z.string().optional(),
});

export type AsanaWebhook = z.infer<typeof asanaWebhookSchema>;

export const asanaDefaultProjectSchema = z.object({
  projectGid: z.string().min(1),
  projectName: z.string().optional(),
  sectionGid: z.string().optional(),
  sectionName: z.string().optional(),
});

export type AsanaDefaultProject = z.infer<typeof asanaDefaultProjectSchema>;

export const asanaSchema = z.object({
  enabled: z.boolean().optional(),
  // Sub-toggle for the no-reply@asana.com email path (Postmark inbound).
  // When false, inbound Asana emails are dropped even if the overall Asana
  // integration is enabled. Defaults to true (keeps existing behaviour).
  emailEnabled: z.boolean().optional(),
  // Project-gid allowlist for the inbound Asana email path. When set,
  // emails whose extracted project gid is NOT in this list are dropped.
  // Empty/undefined = process every project (backward compatible).
  allowedProjectGids: z.array(z.string()).optional(),
  commentBehavior: integrationCommentBehaviorSchema.optional(),
  designReviewRules: z.array(designReviewRuleSchema).optional(),
  webhooks: z.array(asanaWebhookSchema).optional(),
  // Default project/folder for AskPuntt task creation. When configured,
  // AskPuntt can create tasks in this project without asking the user
  // to specify a project GID each time.
  defaultProject: asanaDefaultProjectSchema.optional(),
});

export type AsanaIntegration = z.infer<typeof asanaSchema>;

export const emailReplySettingsSchema = z.object({
  replyWithLink: z.boolean().optional(), // Send review link in reply (default: true)
  replyWithComments: z.boolean().optional(), // Include AI comment summary in reply (default: false)
  replyPrompt: z.string().max(2000).optional(), // Instructions for AI reply content
});

export type EmailReplySettings = z.infer<typeof emailReplySettingsSchema>;

export const emailSchema = z.object({
  enabled: z.boolean().optional(), // OFF by default — must be explicitly enabled
  replySettings: emailReplySettingsSchema.optional(),
  allowedDomains: z.array(z.string()).optional(), // Restrict sender domains (empty = all allowed)
  allowedSenderPatterns: z.array(z.string()).optional(), // Regex patterns for sender email filtering
  commentBehavior: integrationCommentBehaviorSchema.optional(),
  inboundAddress: z.string().optional(), // Generated inbound email address
  connectionId: z.string().optional(), // UUID per enterprise
});

export type EmailIntegration = z.infer<typeof emailSchema>;

export const microsoftTeamsSchema = z.object({
  enabled: z.boolean().optional(), // OFF by default — must be explicitly enabled
  nangoConnectionId: z.string().optional(), // Nango OAuth connection id (read Teams data)
  botEnabled: z.boolean().optional(), // Bot has been activated for this tenant
  tenantId: z.string().optional(), // Azure AD tenant id mapped to this enterprise
  connectedAt: z.coerce.date().optional(), // First time the bot received a message
});

export type MicrosoftTeamsIntegration = z.infer<typeof microsoftTeamsSchema>;

export const composioInheritanceSchema = z.object({
  parentEnterpriseId: z.string(),
  inheritedToolkits: z.array(z.string()).default([]),
});

export type ComposioInheritance = z.infer<typeof composioInheritanceSchema>;

/**
 * Killer-flow reviewer function identifiers. The killer-flow stream
 * renders a column per enabled function; toggling one off in the demo
 * settings hides that column. Names match the canned
 * `KillerReviewerFunction` values in
 * `services/christo/app/handlers/catalyst/demoAskPunttResponses.ts`
 * (lowercased here for stable storage).
 */
export const demoReviewFunctionSchema = z.enum([
  "brand",
  "agency",
  "regulatory",
  "medical",
  "copy",
]);
export type DemoReviewFunction = z.infer<typeof demoReviewFunctionSchema>;

export const DEFAULT_DEMO_REVIEW_FUNCTIONS: DemoReviewFunction[] = [
  "brand",
  "agency",
  "regulatory",
  "medical",
  "copy",
];

/**
 * Visible labels for the immutable default review functions. Kept here
 * (alongside the enum) so both the FE and BE can detect a custom
 * reviewer whose user-supplied label collides with a default — the
 * collision check is case-insensitive against this list, not the raw
 * ids. The FE service module exports a mirrored `Record<…, string>`
 * for the typed registry.
 */
export const DEFAULT_DEMO_REVIEW_FUNCTION_LABELS: Record<
  DemoReviewFunction,
  string
> = {
  brand: "Brand",
  agency: "Agency",
  regulatory: "Regulatory",
  medical: "Medical",
  copy: "Copy",
};

/**
 * User-defined custom reviewer entries. Additive on top of the five
 * immutable defaults: a demoer who wants the killer-flow narration to
 * attribute findings to "Legal" or "Localization" registers the
 * reviewer here and then references the resulting `label` from their
 * own `scriptedResponses` text. Per-entry `enabled` mirrors how
 * defaults work via membership in `enabledReviewFunctions` so the
 * "show / hide a reviewer in the demo" UX is uniform across both
 * lists.
 *
 * Hard caps on shape:
 *   - `id` is a kebab-case slug minted by the FE (uniqueness enforced
 *     server-side). NEVER allowed to collide with a default id.
 *   - `label` is whatever the demoer typed (post-trim). NEVER allowed
 *     to case-insensitively match a default label — that would make
 *     the column ambiguous in the killer-flow stream and in scripted-
 *     response copy.
 *   - max 20 per enterprise so a runaway add-button can't OOM the
 *     stream renderer or the doc.
 *
 * Reset Demo wipes the entire `demoConfig` subdoc via `$unset` so
 * customs disappear on reset — no separate cleanup needed.
 */
export const demoCustomReviewFunctionSchema = z.object({
  id: z.string().trim().min(1).max(64),
  label: z.string().trim().min(1).max(64),
  enabled: z.boolean(),
});
export type DemoCustomReviewFunction = z.infer<
  typeof demoCustomReviewFunctionSchema
>;

/** Hard cap on the number of custom reviewers per demo enterprise. */
export const DEMO_CUSTOM_REVIEW_FUNCTIONS_MAX = 20;
/** Max length of a custom reviewer id (kebab-case slug). */
export const DEMO_CUSTOM_REVIEW_FUNCTION_ID_MAX_LEN = 64;
/** Max length of a custom reviewer display label. */
export const DEMO_CUSTOM_REVIEW_FUNCTION_LABEL_MAX_LEN = 64;

/**
 * One user-tweakable scripted Ask Puntt response for the demo tenant.
 *
 * The hardcoded matcher in
 * `services/christo/app/handlers/catalyst/demoAskPunttResponses.ts`
 * uses an OR-of-AND substring structure (`triggers: string[][]`) for
 * authored entries, which is too fiddly to expose to a non-technical
 * demoer. This user-facing shape collapses that to a flat list of
 * `triggerPhrases`: every phrase fires the same response on
 * case-insensitive substring match. The matcher converts each phrase
 * to a single-element AND-group at read time so the two paths share
 * the same evaluator.
 *
 * `response` is rendered as Ask Puntt markdown.
 */
export const demoScriptedResponseSchema = z.object({
  id: z.string().min(1),
  triggerPhrases: z
    .array(z.string().trim().min(1))
    .min(1, "At least one trigger phrase is required."),
  response: z.string().trim().min(1, "Response cannot be empty."),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type DemoScriptedResponse = z.infer<typeof demoScriptedResponseSchema>;

/** Hard caps so the editor cannot OOM the matcher or the chat UI. */
export const DEMO_SCRIPTED_RESPONSE_MAX_PHRASE_LEN = 200;
export const DEMO_SCRIPTED_RESPONSE_MAX_RESPONSE_LEN = 8000;
export const DEMO_SCRIPTED_RESPONSE_MAX_PER_ENTERPRISE = 50;

/**
 * One free-form fact the buyer mentions that doesn't fit a structured
 * baseline field. The Christo-side Haiku extractor was ripped out in
 * PR #3488; the only writer of demoBaseline is now rembrandt's
 * `record_demo_baseline` tool, and the buyer's exact verbatim ask was
 * "we should be able to add ANYTHING to that, not just the 3 things
 * in the flow… they want to tell it they give taco bell parties when
 * the team does a good job, then we capture it and track possible
 * costs per taco bell party." This shape is the contract for that
 * "anything goes" capture.
 *
 * Shape rationale:
 *   - `key`: short snake- or kebab-case slug the LLM picks
 *     (`taco_bell_parties`, `qbr_offsite_cost`). Used for replace-by-
 *     key semantics: a second turn that re-states the same fact
 *     overwrites instead of appending. Cap of 80 chars catches LLM
 *     hallucinations early.
 *   - `label`: human-readable label the AI surfaces back to the buyer
 *     ("Taco Bell parties for team wins"). Cap of 120 chars.
 *   - `valueText`: free-form description of the fact ("~$1.2K per
 *     party, ~2 parties per quarter"). Cap of 800 chars so a single
 *     fact can't blow out the system prompt.
 *   - `costPerOccurrenceUsd` / `occurrencesPerMonth`: optional
 *     numeric hints that compute_roi_layer can fold into ROI math
 *     when the buyer asks "how much do those parties cost?". Both
 *     optional — purely textual facts (e.g. "the team is split
 *     across SF and NYC") don't need numbers.
 *   - `capturedAt`: ISO timestamp set by the writer. Optional only
 *     because seed fixtures may pre-populate without one.
 */
export const demoCustomFactSchema = z.object({
  key: z.string().trim().min(1).max(80),
  label: z.string().trim().min(1).max(120),
  valueText: z.string().trim().min(1).max(800),
  costPerOccurrenceUsd: z.number().nonnegative().optional(),
  occurrencesPerMonth: z.number().nonnegative().optional(),
  capturedAt: z.string().optional(),
});
export type DemoCustomFact = z.infer<typeof demoCustomFactSchema>;

/** Hard cap on the number of custom facts per demo enterprise. A
 * runaway LLM that hallucinates new keys on every turn would
 * otherwise OOM the system-prompt projection. Once we hit the cap
 * the oldest entries are dropped at write time.
 */
export const DEMO_CUSTOM_FACTS_MAX = 50;

/**
 * Captured "buyer facts" for the Vitalora demo. Populated during the
 * meeting by rembrandt's `record_demo_baseline` tool — the SOLE
 * writer after PR #3488 ripped out christo's Haiku extractor and
 * the "Baseline for your team" templated card. Every Ask Puntt turn
 * on the Vitalora demo now goes straight to live AI; the AI decides
 * when to call `record_demo_baseline` with either structured fields
 * (`industry`, `volumePerMonth`, etc.) OR a `customFacts` entry for
 * anything else the buyer mentions.
 *
 * Read sites: chat (rembrandt Buyer Context prompt projection),
 * `/roi` dashboard, `getTimeSaved` KPI.
 *
 * Every field is optional. Renderers fall back to deck defaults when
 * a field is absent, so a blank baseline behaves exactly like the
 * pre-capture demo (today's behavior).
 *
 * Reset Demo's existing `$unset: { demoConfig: 1 }` already wipes
 * this subdoc — no new reset code needed.
 *
 * Isolation: schema is permissive (all fields optional), but every
 * write through it is fenced by the doubled `isDemoEnterpriseId AND
 * isVitaloraEnterpriseId` gate. See rembrandt's
 * `RecordDemoBaselineTool.execute` for the gate implementation.
 */
export const demoBaselineSchema = z.object({
  industry: z.string().trim().max(120).optional(),
  volumePerMonth: z.number().int().nonnegative().optional(),
  painPoint: z
    .enum(["launch_slips", "agency_spend", "team_time", "mixed"])
    .optional(),
  reworkRoundsPerAsset: z.number().nonnegative().optional(),
  reviewersPerRound: z.number().nonnegative().optional(),
  minutesPerReviewer: z.number().nonnegative().optional(),
  hoursPerAssetBefore: z.number().nonnegative().optional(),
  hoursPerAssetAfter: z.number().nonnegative().optional(),
  cycleTimeWeeks: z.string().trim().max(40).optional(),
  blendedHourlyRateUsd: z.number().nonnegative().optional(),
  costPerRoundUsd: z.number().nonnegative().optional(),
  revenuePerLaunchDayUsd: z.number().nonnegative().optional(),
  existingToolStack: z.array(z.string().trim().max(80)).optional(),
  topLaunchesPerYear: z.number().nonnegative().optional(),
  revenuePerLaunchUsd: z.number().nonnegative().optional(),
  launchShipsWeeksEarlier: z.number().nonnegative().optional(),
  launchLiftPct: z.number().nonnegative().optional(),
  marketsActive: z.array(z.string().trim().max(40)).optional(),
  pocFundingPath: z
    .enum(["marketing_pnl", "central_ai_initiative", "mixed"])
    .optional(),
  priorityAssetCategories: z.array(z.string().trim().max(80)).optional(),
  labelComplianceAutomationPct: z.number().min(0).max(100).optional(),
  claimsReviewAutomationPct: z.number().min(0).max(100).optional(),
  regulatoryCheckAutomationPct: z.number().min(0).max(100).optional(),
  brandGuidelinesAutomationPct: z.number().min(0).max(100).optional(),
  notes: z.string().trim().max(2000).optional(),
  /**
   * Free-form bag of facts the buyer mentions that don't map to a
   * structured field above. Written by `record_demo_baseline` with
   * append-and-replace-by-key semantics: a new `key` is pushed onto
   * the array; an existing `key` is overwritten in place. Cap of 50
   * enforced at write time. See `demoCustomFactSchema` above for the
   * per-entry contract.
   */
  customFacts: z
    .array(demoCustomFactSchema)
    .max(DEMO_CUSTOM_FACTS_MAX)
    .optional(),
  updatedAt: z.string().optional(),
});
export type DemoBaseline = z.infer<typeof demoBaselineSchema>;

/**
 * Deck-default values for the baseline. Every demo math path reads
 * `demoBaseline.foo ?? DEMO_BASELINE_DEFAULTS.foo`, so the renderers
 * stay identical whether or not the demoer has captured anything yet.
 *
 * Kept here (rather than in `demoBaselineCapture.ts`) so both christo
 * and the rembrandt prompt projection can share the same numbers.
 */
export const DEMO_BASELINE_DEFAULTS = {
  reworkRoundsPerAsset: 4,
  reviewersPerRound: 5,
  minutesPerReviewer: 20,
  hoursPerAssetBefore: 7,
  hoursPerAssetAfter: 2,
  cycleTimeWeeks: "2-3",
  blendedHourlyRateUsd: 200,
  costPerRoundUsd: 5000,
  revenuePerLaunchDayUsd: 50000,
  topLaunchesPerYear: 3,
  revenuePerLaunchUsd: 50000000,
  launchShipsWeeksEarlier: 3,
  launchLiftPct: 15,
  labelComplianceAutomationPct: 89,
  claimsReviewAutomationPct: 74,
  regulatoryCheckAutomationPct: 91,
  brandGuidelinesAutomationPct: 63,
} as const;

/**
 * Per-enterprise demo configuration. Phase 1 of the 3-tier asset flow
 * plan introduced `enabledReviewFunctions`; Phase 2 added pre-run-
 * related toggles; later we added `scriptedResponses` for the "buyer
 * wants to goof with canned Q&A" editor; this revision adds
 * `demoBaseline` for the meeting-time fact capture skill.
 *
 * Stored on the Enterprise doc itself rather than a sibling collection
 * so the read path stays a single doc fetch — the killer-flow handler
 * already loads the enterprise to gate `isDemo`.
 *
 * `Reset Demo` wipes the entire `demoConfig` subdoc so every tweak
 * here snaps back to the seeded defaults together.
 */
export const demoConfigSchema = z.object({
  enabledReviewFunctions: z.array(demoReviewFunctionSchema).optional(),
  customReviewFunctions: z
    .array(demoCustomReviewFunctionSchema)
    .max(DEMO_CUSTOM_REVIEW_FUNCTIONS_MAX)
    .optional(),
  scriptedResponses: z.array(demoScriptedResponseSchema).optional(),
  demoBaseline: demoBaselineSchema.optional(),
});
export type DemoConfig = z.infer<typeof demoConfigSchema>;

export const enterpriseSchema = z.object({
  _id: z.string().optional(),
  name: z.string(),
  slug: z.string().optional(),
  logo: z.string().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  primaryColor: z.string().optional(),
  hero: z.string().optional(),
  profile: z.record(z.unknown()).optional(),
  totalActiveGigs: z.number().optional(),
  totalInactiveGigs: z.number().optional(),
  industries: z.array(z.string()).optional(),
  testimonials: z.array(testimonialSchema).optional(),
  profileSettings: enterpriseProfileSettingsSchema.optional(),
  description: z.string().optional(),
  teams: z.array(enterpriseTeamSchema).optional(),
  integrations: z
    .object({
      wrike: wrikeSchema.optional(),
      asana: asanaSchema.optional(),
      email: emailSchema.optional(),
      "microsoft-teams": microsoftTeamsSchema.optional(),
    })
    .optional(),
  composioInheritance: composioInheritanceSchema.optional(),
  isDemo: z.boolean().optional(),
  demoConfig: demoConfigSchema.optional(),
});

export type Enterprise = z.infer<typeof enterpriseSchema>;

export const defaultAiPersonas: EnterpriseAIPersonas[] = [
  { name: "Brand", enabled: true, default: true },
  { name: "Flex", enabled: false, default: false },
  { name: "Legal", enabled: false, default: false },
  { name: "Copy", enabled: false, default: false },
];
